import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import SEO from '../components/SEO';
import { prepareContent } from '../lib/content';

export default function NewsArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/articles/${slug}`)
      .then(r => { setArticle(r.data); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-pulse space-y-4 max-w-2xl w-full px-4">
          <div className="h-8 bg-[#E2E8F0] rounded w-2/3" />
          <div className="h-4 bg-[#E2E8F0] rounded w-1/3" />
          <div className="h-64 bg-[#E2E8F0] rounded-lg" />
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-['Outfit'] font-semibold text-[#0B1B3D]">Cikk nem található</h2>
          <p className="mt-2 text-[#64748B]">A keresett cikk nem létezik vagy eltávolításra került.</p>
          <Link to="/hirek" className="inline-block mt-6">
            <Button variant="outline" className="border-[#0B1B3D] text-[#0B1B3D]">
              <ArrowLeft className="mr-2" size={16} /> Vissza a hírekhez
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="article-page">
      <SEO
        title={article.title}
        description={article.excerpt}
        image={article.featured_image?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${article.featured_image}` : article.featured_image}
        path={`/hirek/${article.slug}`}
      />
      <section className="py-16 sm:py-20 bg-[#0B1B3D]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/hirek" className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white text-sm mb-6 transition-colors">
            <ArrowLeft size={16} /> Vissza a hírekhez
          </Link>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-['Outfit'] font-semibold text-white tracking-tight">
            {article.title}
          </h1>
          <div className="mt-4 flex items-center gap-2 text-[#94A3B8] text-sm">
            <Calendar size={14} />
            {new Date(article.created_at).toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {article.featured_image && (
            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.featured_image.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${article.featured_image}` : article.featured_image}
                alt={article.title}
                className="w-full h-auto object-cover max-h-[500px]"
              />
            </div>
          )}
          <div
            className="prose prose-lg max-w-none text-[#1A1F2C]"
            dangerouslySetInnerHTML={{ __html: prepareContent(article.content) }}
          />
        </div>
      </section>
    </div>
  );
}
